import {
  MODEL_SETTINGS_QUERY_KEY,
  ModelSettingsEditor,
} from '@/components/admin/reglages/model-settings-editor';
import { PmsSpinner } from '@/components/ui/pms-spinner';
import { useAdminSession, useRejectOn401 } from '@/lib/admin-session';
import {
  fetchModelCatalog,
  fetchModelParams,
  fetchModelSettings,
  modelSettingsApi,
} from '@/lib/api';
import { MODEL_SETTINGS_ID } from '@matter_extractor/shared';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';

/**
 * Réglages > Modèles: the FIVE SEATS of §11.1 - the analyst of noyau.md §7 since
 * #149, the ranger of scenario-succes.md §5 since #177 - and the AI budget of
 * §11.2, behind the operator guard, in READING as in writing.
 *
 * Since issue #192 (vitrine.md section 5) they are ONE versioned object, « Les
 * modèles », a singleton on the layer of the experiences and the texts of the
 * noyaux: the form of the seats is its DRAFT, « Enregistrer une nouvelle
 * version » its one write, and a session freezes the version it starts on - a
 * seat moved here never reaches a session that has begun. The journal of the
 * moves made before the versions stays readable under the form.
 *
 * Changing a model used to be an SSH, an edited `.env` and a restart. The
 * models moved into the base, and the cost of that decision is exactly what
 * this screen carries: free editing removes the pre-registration property of
 * the pinned models, so every move is a VERSION - numbered, dated, signed -
 * and the history is displayed here rather than merely stored.
 *
 * What #61 added to it, and why both belong on THIS page: a seat carries its own
 * two tariffs, because the euro count of §11.2 has to be able to follow a model
 * this repository has never priced; and the budget of §11.2 sits under them,
 * because a ceiling in euros read far from the rates it bounds is a number
 * nobody can judge.
 *
 * The API keys are not on this screen and never will be: they are secrets, they
 * stay in the environment, and a screen that could write one would be a screen
 * that could exfiltrate one.
 *
 * ## Three reads, and the editor waits for the first two
 *
 * The detail of the singleton, and the view beside the form (the seed, the
 * tariffs, the warnings, the old journal): the editor mounts once both have
 * landed, the régime of the texts of the noyaux and its usage. The tiles of
 * #175 are never waited on: the seats draw on the built-in list until it lands,
 * and a failed read leaves them there.
 *
 * ## « Voir » travels in the URL
 *
 * `?version=<id>` opens a version read-only, the régime of every other editor.
 */

type ModelesSearch = { version?: string };

function readSearch(search: Record<string, unknown>): ModelesSearch {
  const raw = search.version;
  return typeof raw === 'string' && raw !== '' ? { version: raw } : {};
}

export const Route = createFileRoute('/admin/reglages/modeles')({
  validateSearch: readSearch,
  component: ReglagesModelesRoute,
});

function ReglagesModelesRoute() {
  const { version } = Route.useSearch();
  const navigate = useNavigate();
  return (
    <ReglagesModelesPage
      version={version ?? null}
      onView={(versionId) =>
        void navigate({ to: '/admin/reglages/modeles', search: { version: versionId } })
      }
      onCloseView={() => void navigate({ to: '/admin/reglages/modeles', search: {} })}
    />
  );
}

export interface ReglagesModelesPageProps {
  /** The version « Voir » opened, from `?version=`, or null. */
  readonly version: string | null;
  readonly onView: (versionId: string) => void;
  readonly onCloseView: () => void;
}

/** EXPORTED for `tests/reglages-modeles.test.tsx`: what this page decides is invisible from below. */
export function ReglagesModelesPage({ version, onView, onCloseView }: ReglagesModelesPageProps) {
  const { email } = useAdminSession();
  /** Bumped by « Recharger le brouillon du serveur », and by nothing else. */
  const [reload, setReload] = useState(0);

  const settings = useQuery({
    queryKey: [MODEL_SETTINGS_QUERY_KEY, email, MODEL_SETTINGS_ID],
    queryFn: fetchModelSettings,
    retry: false,
  });

  // What sits beside the form: the seed, the tariffs, the warnings, the old
  // journal. Not refetched by the autosave - a version never moves it.
  const beside = useQuery({
    queryKey: ['model-params', email],
    queryFn: () => fetchModelParams(),
    retry: false,
  });

  // The tiles (#175). Never waited on: the seats draw on the built-in list
  // until it lands, and a failed read leaves them there - the sentence under
  // the seats says which one is showing. One hour of freshness, the backend's
  // own cache window: refetching sooner would only re-read its memo.
  const catalog = useQuery({
    queryKey: ['model-catalog', email],
    queryFn: () => fetchModelCatalog(),
    retry: false,
    staleTime: 60 * 60 * 1000,
  });

  const viewed = useQuery({
    queryKey: ['model-settings-version', email, version],
    queryFn: () => modelSettingsApi.readVersion(MODEL_SETTINGS_ID, version ?? ''),
    enabled: version !== null && settings.data !== undefined,
    retry: false,
  });

  const rejected = useRejectOn401(settings.error ?? beside.error);

  return (
    <section className="space-y-8 pb-16">
      <header className="space-y-1">
        {/* An `h2` since issue #41: Réglages got its tab bar and its own `h1`
            back with the Personas tab (§11.5), so this page is one of those
            served under that title - and two `h1` on one page name two pages. */}
        <h2 className="font-bold text-3xl text-bleu-nuit">Modèles du dispositif</h2>
        <p className="max-w-3xl text-gray-700 text-sm">
          Cinq sièges - l’interviewer, le scribe, le narrateur, le rangeur, l’analyste - chacun avec
          sa porte, son modèle et son tarif, plus un budget IA par étape.{' '}
          <strong>Un seul objet versionné</strong>, « Les modèles », comme les textes des noyaux :
          une session fige au démarrage la version qu’elle joue. Les clés d’accès ne sont pas sur
          cet écran : ce sont des secrets, elles restent dans l’environnement du serveur.
        </p>
      </header>

      {(settings.isPending || beside.isPending) && !settings.isError && !beside.isError ? (
        <div className="flex justify-center py-12">
          <PmsSpinner label="Chargement des modèles" />
        </div>
      ) : null}

      {(settings.isError || beside.isError) && !rejected ? (
        <p role="alert" className="text-gray-700">
          Les modèles n’ont pas pu être chargés.
        </p>
      ) : null}

      {settings.data === undefined || beside.data === undefined ? null : (
        <ModelSettingsEditor
          // Existence and a counter, never the draft's stamp: the editor seeds
          // its draft from the detail ONCE, so a reload of a stale draft has to
          // be a REMOUNT, and a key carrying the stamp would remount under the
          // caret at every autosave.
          key={`modeles-${reload}`}
          detail={settings.data}
          viewing={viewed.data ?? null}
          view={beside.data}
          catalogView={
            // A failed read is said as one - both doors unread - and not as a
            // read still in flight, which is what an absent view would claim
            // forever.
            catalog.isError
              ? {
                  doors: { anthropic: 'unavailable', openrouter: 'unavailable' },
                  fetchedAt: null,
                  entries: [],
                }
              : catalog.data
          }
          onView={(summary) => onView(summary.id)}
          onCloseView={onCloseView}
          onReload={() => {
            // The bump waits for the answer: a remount before it lands would
            // re-seed the editor from the stale draft it is escaping.
            void settings.refetch().then(() => setReload((was) => was + 1));
          }}
        />
      )}
    </section>
  );
}
